'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  canvasToBlob,
  createCleanorExportFileName,
  formatFileSize,
  getRenderScale,
  isImageFile,
  isPdfFile,
  openPdfDocument,
  renderPdfPageToCanvas,
  triggerBrowserDownload,
  type PdfJsDocument,
} from '@/lib/browser-pdf-tools';
import {
  countWords,
  createEnglishOcrWorker,
  normalizeRecognizedText,
  type BrowserOcrProgress,
  type TesseractWorkerLike,
} from '@/lib/browser-ocr';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type SearchableScanPage = {
  id: string;
  label: string;
  previewUrl: string;
  width: number;
  height: number;
  sourceFile: File;
  sourceKind: 'image' | 'pdf';
  sourcePageNumber?: number;
};

type ExportProgress = {
  current: number;
  total: number;
  label: string;
};

type OcrWord = {
  text: string;
  bbox?: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  };
};

function loadImageFromUrl(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('This image could not be decoded in the browser.'));
    image.src = url;
  });
}

async function createImagePage(file: File): Promise<SearchableScanPage> {
  const previewUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageFromUrl(previewUrl);

    return {
      id: crypto.randomUUID(),
      label: file.name,
      previewUrl,
      width: image.naturalWidth,
      height: image.naturalHeight,
      sourceFile: file,
      sourceKind: 'image',
    };
  } catch (error) {
    URL.revokeObjectURL(previewUrl);
    throw error;
  }
}

async function createPdfPages(file: File) {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    const pages: SearchableScanPage[] = [];

    for (let pageIndex = 0; pageIndex < document.numPages; pageIndex += 1) {
      const pageNumber = pageIndex + 1;
      const page = await document.getPage(pageNumber);

      try {
        const baseViewport = page.getViewport({ scale: 1 });
        const previewScale = Math.max(
          0.65,
          Math.min(0.95, 260 / Math.max(baseViewport.width, baseViewport.height)),
        );
        const { canvas } = await renderPdfPageToCanvas(page, previewScale);
        const blob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
        const previewUrl = URL.createObjectURL(blob);
        canvas.width = 0;
        canvas.height = 0;

        pages.push({
          id: crypto.randomUUID(),
          label: `${file.name} • page ${pageNumber}`,
          previewUrl,
          width: Math.round(baseViewport.width),
          height: Math.round(baseViewport.height),
          sourceFile: file,
          sourceKind: 'pdf',
          sourcePageNumber: pageNumber,
        });
      } finally {
        page.cleanup?.();
      }
    }

    return pages;
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

async function renderImageFile(file: File) {
  const previewUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageFromUrl(previewUrl);
    const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);
    const scale = longestEdge > 2200 ? 2200 / longestEdge : 1;
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = window.document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Your browser could not render this image.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.86);
    canvas.width = 0;
    canvas.height = 0;

    return { blob, width, height };
  } finally {
    URL.revokeObjectURL(previewUrl);
  }
}

async function renderPdfSourcePage(document: PdfJsDocument, pageNumber: number) {
  const page = await document.getPage(pageNumber);

  try {
    const baseViewport = page.getViewport({ scale: 1 });
    const renderScale = getRenderScale(baseViewport, 2.05);
    const { canvas, viewport } = await renderPdfPageToCanvas(page, renderScale);
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.86);
    canvas.width = 0;
    canvas.height = 0;

    return {
      blob,
      width: Math.round(viewport.width),
      height: Math.round(viewport.height),
    };
  } finally {
    page.cleanup?.();
  }
}

function getPdfDocumentKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function buildInvisibleTextLayer(
  page: import('pdf-lib').PDFPage,
  font: import('pdf-lib').PDFFont,
  words: OcrWord[],
  pageWidth: number,
  pageHeight: number,
) {
  for (const word of words) {
    const text = word.text?.trim();
    const bbox = word.bbox;

    if (!text || !bbox) {
      continue;
    }

    const boxWidth = Math.max(1, bbox.x1 - bbox.x0);
    const boxHeight = Math.max(1, bbox.y1 - bbox.y0);
    const rawFontSize = Math.max(5, boxHeight * 0.9);
    const measuredWidth = font.widthOfTextAtSize(text, rawFontSize);
    const fittedFontSize =
      measuredWidth > 0 ? Math.max(4, rawFontSize * (boxWidth / measuredWidth)) : rawFontSize;

    page.drawText(text, {
      x: Math.max(0, bbox.x0),
      y: Math.max(0, pageHeight - bbox.y1),
      size: fittedFontSize,
      font,
      color: rgb(1, 1, 1),
      opacity: 0.004,
      maxWidth: pageWidth,
    });
  }
}

function createExportFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return createCleanorExportFileName('searchable-scan-to-pdf', [pageLabel], 'pdf');
}

export default function BrowserSearchableScanToPdf() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const workerRef = useRef<TesseractWorkerLike | null>(null);
  const currentPageLabelRef = useRef('page 1');
  const pdfDocumentCacheRef = useRef<Map<string, PdfJsDocument>>(new Map());
  const pagesRef = useRef<SearchableScanPage[]>([]);
  const [pages, setPages] = useState<SearchableScanPage[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [wordCount, setWordCount] = useState<number | null>(null);

  const totalSourceSize = useMemo(() => {
    const seen = new Set<string>();
    return pages.reduce((sum, page) => {
      const key = getPdfDocumentKey(page.sourceFile);
      if (seen.has(key)) {
        return sum;
      }
      seen.add(key);
      return sum + page.sourceFile.size;
    }, 0);
  }, [pages]);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    return () => {
      pagesRef.current.forEach((page) => URL.revokeObjectURL(page.previewUrl));
      void workerRef.current?.terminate();
      workerRef.current = null;

      void Promise.all(
        Array.from(pdfDocumentCacheRef.current.values()).map(async (document) => {
          document.cleanup?.();
          await document.destroy?.();
        }),
      );
      pdfDocumentCacheRef.current.clear();
    };
  }, []);

  async function addFiles(fileList: FileList | null) {
    if (!fileList?.length) {
      return;
    }

    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setWordCount(null);

    try {
      const nextPages: SearchableScanPage[] = [];

      for (const file of Array.from(fileList)) {
        if (isImageFile(file)) {
          nextPages.push(await createImagePage(file));
          continue;
        }

        if (isPdfFile(file)) {
          nextPages.push(...(await createPdfPages(file)));
          continue;
        }

        throw new Error(`"${file.name}" is not supported. Use image files or standard PDF files.`);
      }

      setPages((current) => [...current, ...nextPages]);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Those files could not be opened.');
    } finally {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }

  function clearPages() {
    setPages((current) => {
      current.forEach((page) => URL.revokeObjectURL(page.previewUrl));
      return [];
    });
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress(null);
    setResultSize(null);
    setWordCount(null);
  }

  function movePage(pageId: string, direction: -1 | 1) {
    setPages((current) => {
      const index = current.findIndex((page) => page.id === pageId);
      const nextIndex = index + direction;

      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [page] = next.splice(index, 1);
      next.splice(nextIndex, 0, page);
      return next;
    });
  }

  function removePage(pageId: string) {
    setPages((current) => {
      const match = current.find((page) => page.id === pageId);
      if (match) {
        URL.revokeObjectURL(match.previewUrl);
      }
      return current.filter((page) => page.id !== pageId);
    });
  }

  async function getPdfDocument(file: File) {
    const key = getPdfDocumentKey(file);
    const cached = pdfDocumentCacheRef.current.get(key);

    if (cached) {
      return cached;
    }

    const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));
    pdfDocumentCacheRef.current.set(key, document);
    return document;
  }

  async function getRenderablePage(pageItem: SearchableScanPage) {
    if (pageItem.sourceKind === 'image') {
      return renderImageFile(pageItem.sourceFile);
    }

    const document = await getPdfDocument(pageItem.sourceFile);
    return renderPdfSourcePage(document, pageItem.sourcePageNumber ?? 1);
  }

  async function exportSearchablePdf() {
    if (!pages.length) {
      setErrorMessage('Choose images or a scanned PDF first.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({ current: 0, total: pages.length, label: 'Preparing OCR…' });

    try {
      const credits = await consumeToolUsage(
        'searchable-scan-to-pdf',
        'Credits are not available for another searchable scan export right now.',
      );

      const worker = await createEnglishOcrWorker((ocrProgress: BrowserOcrProgress) => {
        setProgress((current) =>
          current
            ? {
                ...current,
                label: `${currentPageLabelRef.current} • ${ocrProgress.label}`,
              }
            : null,
        );
      });
      workerRef.current = worker;

      const document = await PDFDocument.create();
      const font = await document.embedFont(StandardFonts.Helvetica);
      let totalWords = 0;

      for (let index = 0; index < pages.length; index += 1) {
        const pageItem = pages[index];
        currentPageLabelRef.current = `Page ${index + 1} of ${pages.length}`;
        setProgress({
          current: index + 1,
          total: pages.length,
          label: `${currentPageLabelRef.current} • Rendering source…`,
        });

        const rendered = await getRenderablePage(pageItem);
        const jpegBytes = new Uint8Array(await rendered.blob.arrayBuffer());
        const image = await document.embedJpg(jpegBytes);
        const pdfPage = document.addPage([rendered.width, rendered.height]);
        pdfPage.drawImage(image, {
          x: 0,
          y: 0,
          width: rendered.width,
          height: rendered.height,
        });

        const result = await worker.recognize(rendered.blob);
        const resultData = result.data as unknown as { text?: string; words?: OcrWord[] };
        const normalizedText = normalizeRecognizedText(resultData.text ?? '');
        const words = resultData.words ?? [];
        totalWords += countWords(normalizedText);
        buildInvisibleTextLayer(pdfPage, font, words, rendered.width, rendered.height);
      }

      const bytes = await document.save();
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setResultSize(blob.size);
      setWordCount(totalWords);
      triggerBrowserDownload(url, createExportFileName(pages.length));
      URL.revokeObjectURL(url);
      setCreditMessage(formatToolCreditSuccess('Searchable PDF export', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'A searchable PDF could not be created from these pages.',
      );
    } finally {
      setProgress(null);
      setIsExporting(false);
      await workerRef.current?.terminate();
      workerRef.current = null;
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Turn scans into a searchable PDF</h2>
          <p>
            Upload document images or a scanned PDF, run OCR in the browser, and export one PDF with
            a hidden searchable text layer on top of the scanned pages.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void addFiles(event.dataTransfer.files);
            }}
            type="button"
          >
            <div>
              <strong>
                {pages.length
                  ? `${pages.length} scan page${pages.length === 1 ? '' : 's'} ready`
                  : 'Choose scan images or drop a scanned PDF here'}
              </strong>
              <span>
                {pages.length
                  ? `${formatFileSize(totalSourceSize)} source total • reorder pages before export`
                  : 'Works with JPG, PNG, WebP, and standard scanned PDF files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*,application/pdf,.pdf"
            className="tool-live-file-input"
            multiple
            onChange={(event) => void addFiles(event.target.files)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!pages.length || isExporting}
              onClick={() => void exportSearchablePdf()}
              type="button"
            >
              {isExporting ? 'Creating searchable PDF…' : 'Create searchable PDF'}
            </button>
            {pages.length ? (
              <button className="button button-ghost" onClick={clearPages} type="button">
                Clear pages
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-pdf-progress">
              <span>{progress.label}</span>
              <progress max={progress.total} value={progress.current} />
            </div>
          ) : null}
          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Scan-first OCR export</h3>
          <div className="tool-mockup-chip-list">
            <span>Scans → PDF</span>
            <span>OCR layer</span>
            <span>Page order</span>
            <span>Local export</span>
          </div>
          <p>
            This keeps the scanned page visuals intact, then adds hidden text so the exported PDF
            becomes searchable and more useful in downstream workflows.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Works best with scan-style pages that have readable text blocks. Handwriting and very
              low-quality photos can reduce OCR accuracy.
            </span>
          </div>
          {pages.length ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{pages.length}</span>
              </div>
              <div>
                <strong>Source total</strong>
                <span>{formatFileSize(totalSourceSize)}</span>
              </div>
              {wordCount !== null ? (
                <div>
                  <strong>OCR words</strong>
                  <span>{wordCount}</span>
                </div>
              ) : null}
              {resultSize ? (
                <div>
                  <strong>Last export</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {pages.length ? (
        <div className="tool-live-preview tool-pdf-preview">
          <h3>Pages</h3>
          <div className="tool-pdf-page-list">
            {pages.map((page, index) => (
              <article className="tool-pdf-page-card" key={page.id}>
                <div className="tool-pdf-page-thumb">
                  <img alt="" src={page.previewUrl} />
                </div>
                <div className="tool-pdf-page-copy">
                  <strong>{`${index + 1}. ${page.label}`}</strong>
                  <span>{`${page.width} × ${page.height} • ${page.sourceKind === 'pdf' ? 'Scanned PDF page' : 'Image scan'}`}</span>
                  <span>Pages export from top to bottom in this order.</span>
                </div>
                <div className="tool-pdf-page-actions">
                  <button
                    className="tool-pdf-page-action"
                    disabled={index === 0}
                    onClick={() => movePage(page.id, -1)}
                    type="button"
                  >
                    Up
                  </button>
                  <button
                    className="tool-pdf-page-action"
                    disabled={index === pages.length - 1}
                    onClick={() => movePage(page.id, 1)}
                    type="button"
                  >
                    Down
                  </button>
                  <button
                    className="tool-pdf-page-action tool-pdf-page-action-danger"
                    onClick={() => removePage(page.id)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}
